import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { useProducts } from '../ProductContext';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { usePrinting } from '../PrintingContext';
import { useDamagedProducts } from '../DamagedProductsContext';
import { useCash } from '../CashContext';
import { useManagedUsers } from '../UsersContext';
import {
  canModerateBranch,
  canViewFinancials,
  firstDisplayNameFromUser,
  normalizeRole,
} from '../utils/userLocale';
import {
  requestPendingApprovalsFocus,
  requestReportDailyDate,
  requestExpensesSummaryPrefs,
} from '../utils/navigationFocus';
import { dateKeyFromString } from '../utils/reportDates';
import {
  businessDayBreakdown,
  buildLast7DayKeys,
  sumBreakdownOverSeries,
  weeklyStackedSeries,
} from '../utils/dashboardAnalytics';
import { useOtherServiceSales } from '../OtherServiceSalesContext';
import { isLowStock } from '../utils/lowStock';
import DashboardAnalytics from '../components/DashboardAnalytics';
import OverviewStatCards, { overviewDamagedStack, overviewMoneyValue } from '../components/OverviewStatCards';
import { getLocalDateKey } from '../utils/reportDates';
import { apiJson } from '../apiClient';
import OverviewCommandPanel, { readOverviewBranchFilter } from '../components/OverviewCommandPanel';
import { useLanguage } from '../i18n/LanguageContext';
import './DashboardPage.css';
import FactoryDashboardPage from './FactoryDashboardPage';
import AutomotiveDashboardPage from './AutomotiveDashboardPage';
import { countPharmacyExpiryAlerts } from '../constants/pharmacyStockCategories';
import { isAutomotiveBusiness, isBrickFactoryUser, isBarPubUser, isPharmacyUser } from '../utils/businessType';
import { hasTenantModule } from '../utils/tenantModules';

function greetingForHour(t, date = new Date()) {
  const h = date.getHours();
  if (h < 12) return t('dash.greetingMorning');
  if (h < 17) return t('dash.greetingAfternoon');
  return t('dash.greetingEvening');
}

function formatTsh(value) {
  return Math.round(Number(value) || 0).toLocaleString();
}

const moneyValue = overviewMoneyValue;

const QUICK_LINKS = [
  { navKey: 'pos-sale-products', title: 'Sale', variant: 'blue', module: 'pos' },
  { navKey: 'stock-add', title: 'Product', variant: 'teal', module: 'stock' },
  { navKey: 'stock-list', title: 'Stock', variant: 'slate', module: 'stock' },
  { navKey: 'debts-list', title: 'Debts', variant: 'orange', module: 'debts' },
  { navKey: 'printing-new-job', title: 'Print', variant: 'purple', module: 'printing' },
  { navKey: 'cash-new', title: 'Wakala', variant: 'green', module: 'cashbook' },
  { navKey: 'reports', title: 'Reports', variant: 'coral', module: 'reports' },
];

function StandardDashboardPage({ user, onNavigate }) {
  const { t } = useLanguage();
  const { products } = useProducts();
  const { salesHistory, salesLoading } = useContext(SalesHistoryContext);
  const { printJobs } = usePrinting();
  const { transactions } = useCash();
  const { sales: otherServiceSales } = useOtherServiceSales();
  const { users: managedTeam } = useManagedUsers();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const canViewExpenses = canModerateBranch(user?.role);
  const showFinancials = canViewFinancials(user?.role);
  const pharmacyMode = isPharmacyUser(user);
  const pharmacyExpiry = useMemo(
    () => (pharmacyMode ? countPharmacyExpiryAlerts(products) : null),
    [pharmacyMode, products],
  );
  const [branchId, setBranchId] = useState(() => (isOwner ? readOverviewBranchFilter() : ''));
  const { damaged } = useDamagedProducts();

  // Count damaged products by status
  const damagedAccepted = damaged.filter(d => d.status === 'approved').length;
  const damagedRejected = damaged.filter(d => d.status === 'rejected').length;
  const damagedPending = damaged.filter(d => d.status === 'pending').length;
  const quickLinks = useMemo(() => {
    let links = QUICK_LINKS.filter((l) => !l.module || hasTenantModule(user, l.module));
    if (!isOwner) links = links.filter((l) => l.navKey !== 'reports');
    return links;
  }, [isOwner, user]);

  const totalProducts = products ? products.length : 0;
  const lowStock = products ? products.filter(isLowStock).length : 0;
  const employees = isOwner ? managedTeam.length : 0;
  const todayKey = getLocalDateKey();
  const salesToday = useMemo(() => {
    if (!salesHistory?.length) return [];
    return salesHistory.filter((s) => dateKeyFromString(s.date) === todayKey);
  }, [salesHistory, todayKey]);

  const todaySalesCount = salesToday.length;
  const todayRevenue = salesToday.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const todayProfit = useMemo(
    () => businessDayBreakdown(salesHistory, printJobs, transactions, todayKey, otherServiceSales).total,
    [salesHistory, printJobs, transactions, todayKey, otherServiceSales]
  );

  /** Sum of Expenses module entries dated today (boss + staff + office). */
  const [debtSummary, setDebtSummary] = useState({ openCount: 0, totalOwedTzs: 0 });
  useEffect(() => {
    if (!hasTenantModule(user, 'debts')) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ status: 'open' });
        if (isOwner && branchId) qs.set('branchId', branchId);
        const data = await apiJson(`/customer-debts?${qs.toString()}`);
        if (!cancelled) {
          setDebtSummary({
            openCount: data.summary?.openCount ?? 0,
            totalOwedTzs: data.summary?.totalOwedTzs ?? 0,
          });
        }
      } catch {
        if (!cancelled) setDebtSummary({ openCount: 0, totalOwedTzs: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isOwner, branchId]);

  const [todayExpenseTotal, setTodayExpenseTotal] = useState(0);
  useEffect(() => {
    if (!canViewExpenses) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ window: 'day', date: todayKey });
        const data = await apiJson(`/expenses/summary?${qs.toString()}`);
        const b = data.buckets || {};
        const total =
          (Number(b.boss) || 0) + (Number(b.staff) || 0) + (Number(b.office) || 0);
        const rounded = Math.round((total + Number.EPSILON) * 100) / 100;
        if (!cancelled) setTodayExpenseTotal(rounded);
      } catch {
        if (!cancelled) setTodayExpenseTotal(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewExpenses, todayKey]);

  const todayNetProfit = useMemo(
    () => Math.round((todayProfit - todayExpenseTotal + Number.EPSILON) * 100) / 100,
    [todayProfit, todayExpenseTotal]
  );

  const weekRollingKeys = useMemo(() => buildLast7DayKeys(), []);
  const weekGrossProfit = useMemo(
    () =>
      Math.round(
        (sumBreakdownOverSeries(
          weeklyStackedSeries(salesHistory, printJobs, transactions, weekRollingKeys, otherServiceSales)
        ).total +
          Number.EPSILON) *
          100
      ) / 100,
    [salesHistory, printJobs, transactions, weekRollingKeys, otherServiceSales]
  );

  const [weekExpenseTotal, setWeekExpenseTotal] = useState(0);
  useEffect(() => {
    if (!canViewExpenses) return undefined;
    let cancelled = false;
    const keySet = new Set(weekRollingKeys);
    (async () => {
      try {
        const data = await apiJson('/expenses');
        const rows = Array.isArray(data.expenses) ? data.expenses : [];
        let sum = 0;
        for (const e of rows) {
          const dk = String(e.expenseDate || '').slice(0, 10);
          if (keySet.has(dk)) sum += Number(e.amount) || 0;
        }
        const rounded = Math.round((sum + Number.EPSILON) * 100) / 100;
        if (!cancelled) setWeekExpenseTotal(rounded);
      } catch {
        if (!cancelled) setWeekExpenseTotal(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewExpenses, weekRollingKeys]);

  const weekNetProfit = useMemo(
    () => Math.round((weekGrossProfit - weekExpenseTotal + Number.EPSILON) * 100) / 100,
    [weekGrossProfit, weekExpenseTotal]
  );

  const recentActivities = salesToday.slice(0, 5);

  const todaySalesTotals = useMemo(() => {
    return salesToday.reduce(
      (acc, s) => {
        const qty = Number(s.quantity) || 0;
        const amount = parseFloat(s.amount) || 0;
        const cost = parseFloat(s.cost);
        const costN = Number.isNaN(cost) ? 0 : cost;
        let profit = parseFloat(s.profit);
        if (Number.isNaN(profit)) {
          const a = parseFloat(s.amount);
          const c = parseFloat(s.cost);
          profit = !Number.isNaN(a) && !Number.isNaN(c) ? a - c : 0;
        }
        return {
          qty: acc.qty + qty,
          amount: acc.amount + amount,
          cost: acc.cost + costN,
          profit: acc.profit + profit,
        };
      },
      { qty: 0, amount: 0, cost: 0, profit: 0 }
    );
  }, [salesToday]);

  const firstName = firstDisplayNameFromUser(user);
  const greet = greetingForHour(t);
  const go = useCallback(
    (key) => {
      if (typeof onNavigate === 'function') onNavigate(key);
    },
    [onNavigate]
  );

  const goToPendingProducts = useCallback(() => {
    requestPendingApprovalsFocus();
    go('stock-list');
  }, [go]);

  const goStatCard = useCallback(
    (c) => {
      if (typeof c.preNavigate === 'function') c.preNavigate();
      go(c.navKey);
    },
    [go]
  );

  const handleOverviewCardClick = useCallback(
    (c) => {
      if (c.pending) {
        goToPendingProducts();
        return;
      }
      goStatCard(c);
    },
    [goStatCard, goToPendingProducts]
  );

  const overviewStatCards = useMemo(() => {
    const rows = [
      {
        id: 'products',
        title: 'Total Products',
        value: totalProducts,
        navKey: 'stock-list',
        border: 'purple',
        hint: 'Open product list',
      },
    ];
    if (showFinancials) {
      rows.push(
        {
          id: 'sales',
          title: "Today's sales",
          caption: todayKey,
          value: todaySalesCount,
          navKey: 'pos-sale-history',
          border: 'green',
          hint: 'Sale count for today.',
        },
        {
          id: 'revenue',
          title: "Today's revenue",
          caption: todayKey,
          value: moneyValue(todayRevenue),
          navKey: 'pos-sale-history',
          border: 'blue',
          isMoney: true,
          hint: 'POS revenue for today.',
        }
      );
    }
    if (isOwner) {
      rows.push({
        id: 'profit',
        title: 'Profit Today',
        caption: todayKey,
        value: moneyValue(todayProfit),
        navKey: 'report-daily',
        preNavigate: () => requestReportDailyDate(todayKey),
        border: 'teal',
        isMoney: true,
        hint: 'Profit across channels for today.',
      });
      rows.push({
        id: 'team',
        title: 'Team',
        value: employees,
        navKey: 'users-list',
        border: 'orange',
        hint: 'Open team / users',
      });
    }
    if (canViewExpenses) {
      rows.push({
        id: 'net-profit',
        title: "Today's net profit",
        caption: todayKey,
        value: moneyValue(todayNetProfit),
        navKey: 'expenses-summary',
        preNavigate: () =>
          requestExpensesSummaryPrefs({ window: 'day', anchorDate: todayKey }),
        border: 'slate',
        isMoney: true,
        hint: 'Profit minus expenses for today.',
      });
      rows.push({
        id: 'week-net-profit',
        title: 'This week net profit (rolling 7 days)',
        caption: `${weekRollingKeys[0]} → ${weekRollingKeys[6]}`,
        value: moneyValue(weekNetProfit),
        navKey: 'expenses-summary',
        preNavigate: () => requestExpensesSummaryPrefs({ window: 'week', anchorDate: todayKey }),
        border: 'purple',
        isMoney: true,
        hint: 'Weekly net profit for last 7 days.',
      });
    }
    if (showFinancials && hasTenantModule(user, 'debts')) {
      rows.push({
        id: 'customer-debts',
        title: 'Customer debts',
        caption: debtSummary.openCount ? `${debtSummary.openCount} open` : 'No open tabs',
        value: moneyValue(debtSummary.totalOwedTzs),
        navKey: 'debts-list',
        border: 'orange',
        isMoney: true,
        hint: 'Open bar tabs / credit balances.',
      });
    }
    if (!isBarPubUser(user)) {
      rows.push({
        id: 'damaged',
        title: 'Damaged Products',
        value: overviewDamagedStack({
          accepted: damagedAccepted,
          rejected: damagedRejected,
          pending: damagedPending,
        }),
        border: 'red',
        hint: 'Damaged products status overview',
        navKey: 'damaged-products',
      });
    }
    if (pharmacyMode && pharmacyExpiry && pharmacyExpiry.total > 0) {
      rows.push({
        id: 'pharmacy-expiry',
        title: 'Expiry alerts',
        caption: `${pharmacyExpiry.expired} expired · ${pharmacyExpiry.soon} within 30 days`,
        value: pharmacyExpiry.total,
        navKey: 'stock-list',
        border: 'red',
        hint: 'Review medicines by expiry date on product list',
      });
    }
    rows.push(
      {
        id: 'lowStock',
        title: 'Low Stock',
        value: lowStock,
        navKey: 'stock-alerts',
        border: 'yellow',
        hint: pharmacyMode ? 'Low quantity on medicines' : 'Open low stock alerts',
      }
    );
    return rows;
  }, [
    totalProducts,
    todaySalesCount,
    todayRevenue,
    todayProfit,
    todayNetProfit,
    weekNetProfit,
    weekRollingKeys,
    todayKey,
    isOwner,
    canViewExpenses,
    showFinancials,
    employees,
    lowStock,
    pharmacyMode,
    pharmacyExpiry,
    damagedAccepted,
    damagedRejected,
    damagedPending,
    user,
    debtSummary,
  ]);

  return (
    <div className="dashboard-root dashboard-root--overview page-bleed">
      <header className="dashboard-welcome">
        <h1 className="dashboard-welcome-greeting">
          {greet}, {firstName}.
        </h1>
        <p className="dashboard-welcome-sub">
          {t('dash.welcome')}
        </p>
      </header>

      {isOwner ? (
        <OverviewCommandPanel
          user={user}
          onNavigate={go}
          branchId={branchId}
          onBranchChange={setBranchId}
        />
      ) : null}

      <OverviewStatCards cards={overviewStatCards} onCardClick={handleOverviewCardClick} />

      <section className="dashboard-quick-wrap" aria-label="Quick actions">
        <h2 className="dashboard-quick-title">{t('dash.actions')}</h2>
        <div className="dashboard-quick-cards-row">
          {quickLinks.map((link) => (
            <button
              key={link.navKey}
              type="button"
              className={`dashboard-quick-card dashboard-quick-card--${link.variant}`}
              onClick={() => go(link.navKey)}
            >
              <span className="dashboard-quick-card-title">{link.title}</span>
            </button>
          ))}
        </div>
      </section>

      {showFinancials ? (
        <DashboardAnalytics
          salesHistory={salesHistory}
          salesLoading={salesLoading}
          printJobs={printJobs}
          transactions={transactions}
          otherServiceSales={otherServiceSales}
          products={products || []}
        />
      ) : null}

      {showFinancials ? (
        <div className="dashboard-recent-activities">
          <h3>{t('dash.todaysSales')}</h3>
          <div className="recent-activities-scroll">
            <table className="recent-activities-table recent-activities-table--simple">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Payment</th>
                  <th>Amount (TZS)</th>
                  {isOwner ? (
                    <>
                      <th>Cost (TZS)</th>
                      <th>Profit (TZS)</th>
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {salesLoading && (!salesHistory || salesHistory.length === 0) ? (
                  <tr>
                    <td colSpan={isOwner ? 7 : 5} className="recent-empty">
                      Loading recent sales…
                    </td>
                  </tr>
                ) : recentActivities.length === 0 ? (
                  <tr>
                    <td colSpan={isOwner ? 7 : 5} className="recent-empty">
                      No sales recorded for today yet. Record a sale from POS, or open Reports / sale history for other
                      days.
                    </td>
                  </tr>
                ) : (
                  recentActivities.map((s, i) => (
                    <tr key={s.id != null ? String(s.id) : `row-${i}`}>
                      <td>{s.date}</td>
                      <td>{s.product?.name || '—'}</td>
                      <td>{s.quantity ?? '—'}</td>
                      <td>{s.paymentLabel || s.paymentMethod || '—'}</td>
                      <td>{s.amount != null ? formatTsh(s.amount) : '—'}</td>
                      {isOwner ? (
                        <>
                          <td>{s.cost != null ? formatTsh(s.cost) : '—'}</td>
                          <td>{s.profit != null ? formatTsh(s.profit) : '—'}</td>
                        </>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
              {salesToday.length > 0 ? (
                <tfoot>
                  <tr className="recent-activities-totals">
                    <td colSpan={4}>
                      <strong>Today&apos;s totals</strong>
                      <span className="recent-activities-totals-note">
                        {' '}
                        · Qty {todaySalesTotals.qty.toLocaleString()} ({salesToday.length} sale{salesToday.length === 1 ? '' : 's'})
                      </span>
                    </td>
                    <td>
                      <strong>{Math.round(todaySalesTotals.amount).toLocaleString()}</strong>
                    </td>
                    {isOwner ? (
                      <>
                        <td>
                          <strong>{Math.round(todaySalesTotals.cost).toLocaleString()}</strong>
                        </td>
                        <td>
                          <strong>{Math.round(todaySalesTotals.profit).toLocaleString()}</strong>
                        </td>
                      </>
                    ) : null}
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DashboardPage = (props) => {
  if (isAutomotiveBusiness(props.user)) {
    return <AutomotiveDashboardPage user={props.user} onNavigate={props.onNavigate} />;
  }
  if (isBrickFactoryUser(props.user)) {
    return <FactoryDashboardPage user={props.user} onNavigate={props.onNavigate} />;
  }
  return <StandardDashboardPage {...props} />;
};

export default DashboardPage;
